import React, { useEffect, useRef } from 'react';
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGS, DEFAULT_LANG } from '../../routes/routeMap';

/**
 * Wraps all locale-prefixed routes. Reads :lng from the URL,
 * validates it, syncs i18next, and sets <html lang>.
 */
const LocaleLayout = () => {
  const { lng } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Reserved top-level paths that should NOT be treated as locale prefixes
  const RESERVED_PATHS = ['ops', 'api'];

  useEffect(() => {
    if (RESERVED_PATHS.includes(lng)) {
      // This is a reserved path, not a locale — let other routes handle it
      return;
    }
    if (!SUPPORTED_LANGS.includes(lng)) {
      // Invalid language prefix → redirect to default language keeping the rest of the path
      const rest = location.pathname.replace(`/${lng}`, '') || '/';
      navigate(`/${DEFAULT_LANG}${rest}`, { replace: true });
      return;
    }

    // Sync i18next language with URL
    if (i18n.language !== lng) {
      i18n.changeLanguage(lng);
    }

    // Update <html lang> attribute
    document.documentElement.lang = lng;

    // Persist choice
    localStorage.setItem('fm_lang', lng);
  }, [lng, i18n, navigate, location.pathname]);

  // Don't render children while redirecting or for reserved paths
  if (!SUPPORTED_LANGS.includes(lng)) {
    return null;
  }

  return (
    <>
      <Outlet />
      <PrerenderSignal />
    </>
  );
};

/**
 * Dispatches 'render-event' ONCE after the page is fully ready for prerender capture.
 *
 * Guards (all must pass before the event fires):
 *   1. i18n.isInitialized — core i18next init complete
 *   2. Language match — i18n.language matches the :lng URL param
 *      (prevents capturing Spanish text on /en/ routes if changeLanguage is in-flight)
 *   3. All namespaces loaded — 'translation', 'nav', 'seo' are all resolved
 *      (prevents raw keys in header/footer/SEO tags)
 *   4. Fire-once ref — the event dispatches only once per page load
 *      (prevents the prerenderer from capturing on an early, incomplete render)
 *
 * After all guards pass, a double rAF ensures react-helmet-async has flushed
 * <title>, <meta>, <link> to <head> before the snapshot.
 */
const REQUIRED_NS = ['translation', 'nav', 'seo'];

const PrerenderSignal = () => {
  const { i18n } = useTranslation();
  const { lng } = useParams();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (!i18n.isInitialized) return;

    // Active language must match URL (guards against mid-changeLanguage snapshots)
    if (lng && i18n.language !== lng) return;

    // All namespaces must be loaded for the current language
    if (!REQUIRED_NS.every((ns) => i18n.hasLoadedNamespace(ns))) return;

    firedRef.current = true;

    // Poll until per-route Suspense has resolved (lazy chunk loaded).
    // PageLoader renders with [data-prerender-loader]; when it disappears
    // the actual page content is in the DOM and safe to capture.
    const t0 = Date.now();
    const waitForContent = () => {
      const spinner = document.querySelector('[data-prerender-loader]');
      if (spinner && Date.now() - t0 < 8000) {
        requestAnimationFrame(waitForContent);
        return;
      }
      // Serialize Emotion CSS into style tags for prerender capture.
      // Emotion uses insertRule() (speedy mode) which adds rules to the
      // CSSOM but leaves <style> textContent empty. Puppeteer's HTML
      // snapshot therefore has no CSS → unstyled first paint → huge CLS.
      // navigator.webdriver is true only in Puppeteer, so this is a no-op
      // on real client visits.
      if (navigator.webdriver) {
        document.querySelectorAll('style[data-emotion]').forEach((node) => {
          if (node.sheet && !node.textContent.trim()) {
            try {
              node.textContent = [...node.sheet.cssRules]
                .map((r) => r.cssText)
                .join('\n');
            } catch (_) { /* cross-origin sheet */ }
          }
        });
      }

      // Content ready (or 8s timeout) — double rAF for Helmet flush
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document.dispatchEvent(new Event('render-event'));
        });
      });
    };
    requestAnimationFrame(waitForContent);
  });
  return null;
};

export default LocaleLayout;
